import * as React from 'react';
import { X } from 'lucide-react';
import { z } from 'zod';
import { cn } from '@/lib/utils';

/**
 * A token/chip input for email addresses.
 *
 * Why this exists rather than a reuse: `ui/multi-select.tsx` picks from a fixed
 * `readonly OptionType[]`, and `ui/freestyle-combobox.tsx` holds a single value.
 * Neither can collect an open-ended list of typed addresses.
 *
 * Deliberately owns nothing but `string[]` — it is used by the client form's
 * accounts-payable field AND by the invoice send dialog, so it must not know
 * about either.
 *
 * Validation matters more than usual here: one malformed address makes the mail
 * provider reject the entire multi-recipient send, so the invoice reaches
 * nobody. This is the first gate; the shared Zod schema validates again at the
 * API boundary.
 */

// Same rule the shared schemas apply, so what the box accepts is exactly what
// the API accepts.
const emailSchema = z.string().trim().email();

export function isValidEmail(candidate: string): boolean {
  return emailSchema.safeParse(candidate).success;
}

// Commit separators. Comma is the one users type; the rest make a paste out of
// a mail client or a spreadsheet land as separate chips instead of one blob.
const SEPARATORS = /[,;\n\r\t]/;

/** One address this box can offer while the user types. */
export interface EmailSuggestion {
  email: string;
  /** Whose address it is — shown beside it, and matched on as well. */
  label: string;
}

export interface EmailChipInputProps {
  value: string[];
  onChange: (value: string[]) => void;
  id?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
  /**
   * Addresses to offer as the user types, Gmail-style. Omitted entirely by the
   * client form's AP field, which has nothing to suggest — so a box with no
   * suggestions behaves exactly as it did before they existed.
   */
  suggestions?: readonly EmailSuggestion[];
}

/** How many matches the list shows before it stops growing. */
const MAX_SUGGESTIONS = 6;

export function EmailChipInput({
  value,
  onChange,
  id,
  placeholder = 'name@example.com',
  disabled = false,
  className,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  suggestions,
}: EmailChipInputProps) {
  const [draft, setDraft] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  /**
   * Which match the keyboard is on. -1 = none, so Enter commits what was typed
   * rather than picking a suggestion nobody moved onto — a user who types a
   * whole address and hits Enter must get that address, not the first thing
   * the list happened to match.
   */
  const [highlight, setHighlight] = React.useState(-1);
  /** Escape closes the list for this draft without closing the dialog around it. */
  const [dismissed, setDismissed] = React.useState(false);

  const query = draft.trim().toLowerCase();
  const chosen = new Set(value.map((email) => email.trim().toLowerCase()));
  const matches =
    query === '' || dismissed
      ? []
      : (suggestions ?? [])
          .filter(
            (option) =>
              !chosen.has(option.email.trim().toLowerCase()) &&
              (option.email.toLowerCase().includes(query) ||
                option.label.toLowerCase().includes(query)),
          )
          .slice(0, MAX_SUGGESTIONS);
  const listId = id ? `${id}-suggestions` : undefined;

  /**
   * Commits every complete address in `raw` and returns what could not be
   * committed, which stays in the box so a typo can be repaired rather than
   * retyped. Valid addresses in a mixed paste are still kept.
   */
  const commit = (raw: string): string => {
    const next = [...value];
    let firstError: string | null = null;
    const leftover: string[] = [];

    for (const part of raw.split(SEPARATORS)) {
      const candidate = part.trim();
      if (!candidate) continue;

      if (!isValidEmail(candidate)) {
        firstError ??= `${candidate} is not a valid email address`;
        leftover.push(candidate);
        continue;
      }
      if (next.some((existing) => existing.toLowerCase() === candidate.toLowerCase())) {
        firstError ??= `${candidate} has already been added`;
        continue;
      }
      next.push(candidate);
    }

    if (next.length !== value.length) onChange(next);
    setError(firstError);
    return leftover.join(', ');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (SEPARATORS.test(raw)) {
      setDraft(commit(raw));
      return;
    }
    setDraft(raw);
    setHighlight(-1);
    setDismissed(false);
    if (error) setError(null);
  };

  /** Commit a suggested address, which needs no validation — it came from us. */
  const pick = (email: string) => {
    if (!chosen.has(email.trim().toLowerCase())) onChange([...value, email]);
    setDraft('');
    setHighlight(-1);
    setDismissed(false);
    setError(null);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (matches.length > 0 && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      // Clamped rather than wrapping, and -1 ("none") is a reachable position:
      // arrowing back off the top returns the user to what they typed, which a
      // wrapping list would make unreachable without clearing the box.
      e.preventDefault();
      setHighlight((current) =>
        e.key === 'ArrowDown'
          ? Math.min(current + 1, matches.length - 1)
          : Math.max(current - 1, -1),
      );
      return;
    }
    if (e.key === 'Escape' && matches.length > 0) {
      // STOPPED HERE, not bubbled: this box lives inside the send dialog, and
      // an Escape that reached it would throw away a half-written email.
      e.preventDefault();
      e.stopPropagation();
      setDismissed(true);
      setHighlight(-1);
      return;
    }
    if (e.key === 'Enter') {
      // Without this the Enter that commits a chip submits the surrounding form.
      e.preventDefault();
      const highlighted = matches[highlight];
      if (highlighted) {
        pick(highlighted.email);
        return;
      }
      setDraft(commit(draft));
      return;
    }
    if (e.key === 'Backspace' && draft === '' && value.length > 0) {
      onChange(value.slice(0, -1));
      setError(null);
    }
  };

  const handleBlur = () => {
    // A typed-but-not-Entered address would otherwise be silently dropped on save.
    if (draft.trim()) setDraft(commit(draft));
  };

  const remove = (email: string) => {
    onChange(value.filter((existing) => existing !== email));
    setError(null);
  };

  return (
    <div className="relative">
      <div
        className={cn(
          'flex min-h-9 w-full flex-wrap items-center gap-1.5 rounded-md border border-input bg-transparent px-2 py-1 text-base shadow-sm transition-colors focus-within:outline-none focus-within:ring-1 focus-within:ring-ring md:text-sm',
          error && 'border-red-500 focus-within:ring-red-500',
          disabled && 'cursor-not-allowed opacity-50',
          className,
        )}
        onClick={() => inputRef.current?.focus()}
      >
        {value.map((email) => (
          <span
            key={email}
            className="inline-flex items-center gap-1 rounded bg-gray-100 px-2 py-0.5 text-sm text-gray-800"
          >
            {email}
            <button
              type="button"
              aria-label={`Remove ${email}`}
              disabled={disabled}
              onClick={(e) => {
                e.stopPropagation();
                remove(email);
              }}
              className="text-gray-500 hover:text-gray-900 disabled:pointer-events-none"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          id={id}
          type="text"
          value={draft}
          disabled={disabled}
          placeholder={value.length === 0 ? placeholder : ''}
          aria-label={ariaLabel}
          aria-describedby={ariaDescribedBy}
          aria-invalid={error ? true : undefined}
          role={suggestions ? 'combobox' : undefined}
          aria-expanded={suggestions ? matches.length > 0 : undefined}
          aria-controls={matches.length > 0 ? listId : undefined}
          aria-autocomplete={suggestions ? 'list' : undefined}
          aria-activedescendant={
            highlight >= 0 && listId ? `${listId}-${highlight}` : undefined
          }
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          className="min-w-[12rem] flex-1 border-0 bg-transparent py-1 outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed"
        />
      </div>
      {matches.length > 0 && (
        <ul
          id={listId}
          role="listbox"
          className="absolute z-50 mt-1 w-full overflow-hidden rounded-md border border-gray-200 bg-white py-1 shadow-md"
        >
          {matches.map((match, index) => (
            <li
              key={match.email}
              id={listId ? `${listId}-${index}` : undefined}
              role="option"
              aria-selected={index === highlight}
              // MOUSEDOWN, not click: the input's blur fires first otherwise,
              // commits the half-typed address as a chip and moves the list out
              // from under the pointer before the click can land on it.
              onMouseDown={(e) => {
                e.preventDefault();
                pick(match.email);
              }}
              onMouseEnter={() => setHighlight(index)}
              className={cn(
                'cursor-pointer px-2 py-1.5 text-sm',
                index === highlight ? 'bg-gray-100' : 'bg-transparent',
              )}
            >
              <span className="block truncate">{match.label}</span>
              <span className="block truncate text-xs text-gray-500">{match.email}</span>
            </li>
          ))}
        </ul>
      )}
      {error && (
        <p role="alert" className="mt-1 text-sm text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}
